<template>
  <div>
    <q-linear-progress
      v-if="progress"
      size="xs"
      :color="progressColor"
      indeterminate
    />
    <div
      ref="scrollTargetRef"
      class="full-width"
      :style="`position:relative;max-height:${height}px;height:${height}px;overflow:auto`"
    >
      <q-infinite-scroll
        ref="infiniteScroll"
        :scroll-target="$refs.scrollTargetRef"
        @load="onInfiniteScrollLoad"
        :offset="offset"
      >
        <div ref="topView"></div>
        <div :class="contentClass">
          <DCard
            @enterSection="enterSection(item)"
            :allowNavigation="allowCardNavigation"
            @preview="cardPreview"
            :ref="`card${item.id}`"
            @cardFocus="blurOtherCards"
            v-for="(item, i) in renderedCards"
            :key="`${uniqueKey}${i}`"
            :layout="layout"
            :content="item"
            :noScrolling="true"
            :loading="loading"
            :apiBaseUrl="apiBaseUrl"
          />
        </div>
      </q-infinite-scroll>
      <div
        v-if="showScrollTopButton"
        style="position: fixed; bottom: 20px; right: 20px"
      >
        <q-btn
          style="color: white; background-color: rgba(0, 0, 0, 0.7)"
          @click="scrollToTop"
          fab
          icon="keyboard_arrow_up"
        >
          <q-tooltip>Scroll to Top</q-tooltip>
        </q-btn>
      </div>
    </div>
    <DCardPreview
      @enterSection="enterSection"
      ref="cardPreview"
      :allowCardNavigation="allowCardNavigation"
      :layout="layout"
      :content="currentPreviewContent"
      :apiBaseUrl="apiBaseUrl"
      v-model="preview"
    />
  </div>
</template>

<script>
import { uid } from "quasar";
import DCard from "./DCard/DCard.vue";
import DCardPreview from "./DCard/DCardPreview.vue";
import "./core-interface";

export default {
  name: "DCardPanel",
  components: {
    DCard,
    DCardPreview,
  },
  props: {
    cards: {
      type: Array,
      default: () => [],
    },
    height: {
      type: Number,
      required: true,
    },
    layout: {
      type: Object,
      default: () => ({}),
    },
    offset: {
      type: Number,
      default: 250,
    },
    pageSize: {
      type: Number,
      default: 25,
    },
    progressColor: {
      type: String,
      default: "grey-8",
    },
    contentClass: {
      type: Object,
      default: () => {
        return {
          row: true,
          "justify-center": true,
          "q-gutter-xl": true,
          "q-pa-lg": true,
        };
      },
    },
    allowCardNavigation: {
      type: Boolean,
      default: true,
    },
    lazy: {
      // Lazy loading
      type: Boolean,
      default: false,
    },
    totalCards: {
      type: Number, // Required to lazy loading
    },
    currentPage: {
      type: Number, // Required to lazy loading
      default: 0,
    },
    externalPreview: {
      type: Boolean, // Required to lazy loading
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    apiBaseUrl: {
      type: Object,
      required: false,
      default: () => {},
    },
  },
  data() {
    return {
      renderedCards: [],
      nextCardIndex: 0,
      showScrollTopButton: false,
      progress: false,
      preview: false,
      currentPreviewContent: null,
    };
  },
  computed: {
    uniqueKey: {
      get() {
        return uid();
      },
    },
  },
  mounted() {
    this.$refs.scrollTargetRef.addEventListener("scroll", (e) => {
      this.showScrollTopButton = e.srcElement.scrollTop > 0;
    });
  },
  watch: {
    cards: {
      handler(val, oldValue) {
        if (this.currentPage <= 1) {
          this.renderedCards = [];
          this.nextCardIndex = 0;
          setTimeout(() => {
            this.$refs.infiniteScroll.resume();
          });
        }
      },
      immediate: false,
    },
    renderedCards(val) {
      if (!val.length) {
        this.preview = false;
      }
    },
  },
  methods: {
    onInfiniteScrollLoad(index, done) {
      const nextPage = this.nextPage(index, done);
      if (nextPage) this.renderedCards = this.renderedCards.concat(nextPage);
      setTimeout(() => {
        done();
      }, 500);
    },
    nextPage(index, done) {
      if (this.cards.length === 0) return [];
      const retval = [];
      const from = this.nextCardIndex;
      const to =
        from + this.pageSize > this.cards.length - 1
          ? this.cards.length - 1
          : from + this.pageSize - 1;
      this.nextCardIndex = to + 1;
      // console.log(
      //   "from: ",
      //   from,
      //   " To: ",
      //   to,
      //   " cards.length: ",
      //   this.cards.length,
      //   " totalCards: ",
      //   this.totalCards
      // );
      if (from >= to) {
        if (
          this.lazy &&
          from <= this.totalCards &&
          this.cards.length != this.totalCards
        ) {
          this.$emit("newPage", { index, done });
          return false;
        } else {
          this.$refs.infiniteScroll.stop();
          this.$emit("didEndScrolling");
        }
      }
      for (let i = from; i <= to; i++) {
        retval.push(this.cards[i]);
      }
      return retval;
    },
    scrollToTop() {
      this.$refs.topView.scrollIntoView({
        behavior: "smooth",
      });
    },
    showProgress() {
      this.progress = true;
    },
    hideProgress() {
      this.progress = false;
    },
    cardPreview(content) {
      this.$emit("preview", content);
      if (!this.externalPreview) this.setPreview(content);
    },
    setPreview(content) {
      this.currentPreviewContent = content;
      this.preview = true;
    },
    blurOtherCards(id) {
      for (const card of this.renderedCards) {
        if (card.id !== id) {
          this.$refs["card" + card.id][0].hideOverlay();
        }
      }
    },
    enterSection(content) {
      this.$emit("enterSection", content);
    },
  },
};
</script>
